import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config';
import { doc, collection, query, where, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { cleanPayload } from '../../firebase/cleanPayload';

import { 
    parseCurrency, 
    formatCurrencyForInput, 
    mapMonedaToDB,
    mapMonedaToVisual
} from '../../utils/segurosUtils';

/**
 * ModalAnulacion - Gestión, Reversión de Deuda y Vinculación de Reemplazos por Anulación (Patrimoniales).
 * Optimizado con consumo de caché global, ordenamiento en memoria y diseño responsivo Tailwind.
 */
const ModalAnulacion = ({ show, onClose, onSuccess, poliza, clientId }) => {
  // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
  const { dbMasters } = useSegurosData();

  // --- 2. ESTADOS LOCALES ---
  const [formData, setFormData] = useState({
    motivoAnulacion: '',
    fechaAnulacion: '',
    moneda: 'ARS $',
    primaAnulacion: '',
    despachoAnulacion: '',
    quienGestiono: '',
    observacionesAnulacion: '', 
    polizaReemplazo: null 
  });
  
  const [anularDeuda, setAnularDeuda] = useState(false); 
  const [isSaving, setIsSaving] = useState(false);

  // Estados para la búsqueda de póliza de reemplazo
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // --- 3. HIDRATACIÓN DINÁMICA DE LA BAJA ---
  useEffect(() => {
    if (show && poliza) {
      setFormData({
        motivoAnulacion: poliza.motivoAnulacion || '',
        fechaAnulacion: poliza.fechaAnulacion || new Date().toISOString().split('T')[0], 
        moneda: mapMonedaToVisual(poliza.moneda || '$'), 
        primaAnulacion: formatCurrencyForInput(poliza.primaAnulacion), 
        despachoAnulacion: poliza.despachoAnulacion || '',
        quienGestiono: poliza.quienGestiono || '',
        observacionesAnulacion: '', 
        polizaReemplazo: poliza.polizaReemplazo || null
      });
      setAnularDeuda(false); 
      setSearchTerm('');
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [show, poliza]);

  // --- 4. MEMORIZACIÓN Y ORDENAMIENTO EN MEMORIA LOCAL (useMemo) ---
  const sortedColaboradores = useMemo(() => {
    return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
        (a.nombre || '').localeCompare(b.nombre || '')
    );
  }, [dbMasters?.colaboradores]);

  // --- 5. LÓGICA DE BÚSQUEDA DE PÓLIZA DE REEMPLAZO (Segmentada) ---
  const handleSearchPoliza = async () => {
    if (!searchTerm.trim()) return;
    setIsSearching(true);
    setHasSearched(false);
    try {
        const polizasRef = collection(db, `public/data/clientes/${clientId}/polizas`);
        const q = query(polizasRef, where('nPoliza', '==', searchTerm.trim()));
        const snap = await getDocs(q);
        const results = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(p => p.id !== poliza.id); 
        
        setSearchResults(results);
        setHasSearched(true);
    } catch (error) {
        console.error("Error buscando póliza de reemplazo:", error);
    } finally {
        setIsSearching(false);
    }
  };

  // --- 6. HANDLERS DE ACCIÓN ---
  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePrimaChange = (e) => {
    let rawValue = e.target.value.replace(/\D/g, '');
    if (rawValue) {
        const numberValue = Number(rawValue) / 100;
        setFormData(prev => ({ ...prev, primaAnulacion: formatCurrencyForInput(numberValue) }));
    } else {
        setFormData(prev => ({ ...prev, primaAnulacion: '' }));
    }
  };

  // --- 7. PROCESAMIENTO Y ANULACIÓN EN BATCH ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);

    try {
      const client_id_safe = clientId || 'client_temp_123';
      const polizaRef = doc(db, `public/data/clientes/${client_id_safe}/polizas`, poliza.id);
      
      const primaAbsoluta = Math.abs(parseCurrency(formData.primaAnulacion));
      const primaNegativa = primaAbsoluta * -1;

      // Armamos payload de actualización de póliza raíz para Firestore
      const updateData = {
        ...formData,
        moneda: mapMonedaToDB(formData.moneda),
        primaAnulacion: primaNegativa,
        estadoEmision: 'ANULADA',
        quienGestiono: formData.quienGestiono || '',
        ultimaModificacion: serverTimestamp()
      };

      // Aplicamos la limpieza profunda de campos vacíos
      const cleanedUpdateData = cleanPayload(updateData, true, false);

      const batch = writeBatch(db);
      
      // Actualizamos la póliza raíz
      batch.set(polizaRef, cleanedUpdateData, { merge: true });

      // Lógica transaccional de anulación de deudas activas asociadas
      if (anularDeuda && poliza?.tieneDeuda) {
          const deudasRef = collection(db, `public/data/clientes/${client_id_safe}/polizas/${poliza.id}/cobranza`);
          const estadosActivos = ['Pendiente', 'Informada', 'Sin Asignar', 'Reclamada', 'CBU Readherido', 'TC Readherido', 'Incobrable'];
          const q = query(deudasRef, where('estado', 'in', estadosActivos));
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
              snapshot.docs.forEach(docSnap => {
                  batch.update(docSnap.ref, { 
                      estado: 'Anulada',
                      ultimaModificacion: serverTimestamp() 
                  });
              });
              // Quitamos de forma segura el flag indicador de morosidad en la póliza raíz
              batch.update(polizaRef, { tieneDeuda: false });
          }
      }

      await batch.commit();

      // Pasamos al callback una fecha JS normal en lugar del FieldValue {_methodName}
      const clientSideData = {
        ...poliza,
        ...cleanedUpdateData,
        ultimaModificacion: new Date().toISOString()
      };

      onSuccess('Póliza anulada correctamente.', clientSideData);
      onClose();
    } catch (error) {
      console.error("Error crítico procesando la anulación de la póliza:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1300] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 animate-fade-in flex flex-col max-h-[90vh] transition-all duration-300">
        
        {/* CABECERA DEL MODAL */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-red-100 bg-red-50 flex-shrink-0 text-left select-none">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-full shadow-sm text-red-500 ring-1 ring-red-100">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                </div>
                <div>
                    <h3 className="text-lg font-black text-slate-800 leading-none">Anular Póliza</h3>
                    <p className="text-[10px] text-red-600 font-black uppercase mt-1.5 tracking-wider">Esta acción es irreversible</p>
                </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white/80 rounded-full">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>

        {/* FORMULARIO DINÁMICO */}
        <form id="anularForm" onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent text-left">
            
            {/* GESTIÓN DE SALDO PENDIENTE */}
            {poliza?.tieneDeuda && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 select-none animate-fade-in space-y-4">
                    <div className="flex gap-3 items-start">
                        <div className="p-2 bg-amber-100 rounded-lg text-amber-800 shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-sm font-black text-amber-950 uppercase tracking-tight">Atención: Contrato con saldo pendiente</h4>
                            <p className="text-xs text-amber-800 font-semibold leading-relaxed">
                                Esta póliza arrastra cuotas impagas en su sección de cobranza. Determine cómo proceder con los saldos deudores de la cuenta corriente antes de confirmar la baja definitiva del contrato:
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 pt-1">
                        <label className={`flex-1 flex items-center gap-3 cursor-pointer p-3.5 rounded-xl border transition-all duration-300 ${!anularDeuda ? 'bg-white border-amber-500 shadow-md ring-2 ring-amber-100' : 'bg-white/40 border-amber-200/60 hover:bg-amber-100/50'}`}>
                            <input type="radio" name="deudaOption" checked={!anularDeuda} onChange={() => setAnularDeuda(false)} className="text-amber-600 focus:ring-amber-500 w-5 h-5 border-slate-200 cursor-pointer" />
                            <div className="flex flex-col text-left">
                                <span className="text-sm font-bold text-slate-800 leading-none">Mantener Deuda</span>
                                <span className="text-[10px] font-semibold text-slate-500 mt-1.5">El saldo emitido permanece como deuda activa y exigible en la ficha del cliente.</span>
                            </div>
                        </label>
                        <label className={`flex-1 flex items-center gap-3 cursor-pointer p-3.5 rounded-xl border transition-all duration-300 ${anularDeuda ? 'bg-white border-amber-500 shadow-md ring-2 ring-amber-100' : 'bg-white/40 border-amber-200/60 hover:bg-amber-100/50'}`}>
                            <input type="radio" name="deudaOption" checked={anularDeuda} onChange={() => setAnularDeuda(true)} className="text-amber-600 focus:ring-amber-500 w-5 h-5 border-slate-200 cursor-pointer" />
                            <div className="flex flex-col text-left">
                                <span className="text-sm font-bold text-slate-800 leading-none">Anular Deuda</span>
                                <span className="text-[10px] font-semibold text-slate-500 mt-1.5">Anular las cuotas de cobranza pendientes para cerrar el saldo actual en $0,00.</span>
                            </div>
                        </label>
                    </div>
                </div>
            )}

            {/* MOTIVO ANULACIÓN */}
            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Motivo de la Anulación *</label>
                <select name="motivoAnulacion" value={formData.motivoAnulacion} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white h-[46px] focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all" required>
                    <option value="">Seleccione un motivo...</option>
                    <option value="VENTA">VENTA</option>
                    <option value="DESESTIMIENTO">DESESTIMIENTO</option>
                    <option value="NO RENUEVA">NO RENUEVA</option>
                    <option value="DUPLICIDAD">DUPLICIDAD</option>
                    <option value="INCOBRABLE">INCOBRABLE</option>
                    <option value="ANULACIÓN POR REEMPLAZO">ANULACIÓN POR REEMPLAZO</option>
                    <option value="ANULACIÓN POR ERROR DE CARGA">ANULACIÓN POR ERROR DE CARGA</option>
                    <option value="FALTA DE PAGO CIA">FALTA DE PAGO CIA</option>
                    <option value="FALTA DE PAGO BROKER">FALTA DE PAGO BROKER</option>
                    <option value="MUDANZA">MUDANZA</option>
                    <option value="CAMBIO DE TITULARIDAD">CAMBIO DE TITULARIDAD</option>
                    <option value="CAMBIO DE PAS">CAMBIO DE PAS</option>
                    <option value="COSTO">COSTO</option>
                    <option value="SINIESTRO">SINIESTRO</option>
                </select>
            </div>

            {/* SECCIÓN CONDICIONAL: PÓLIZA DE REEMPLAZO */}
            {(formData.motivoAnulacion === 'ANULACIÓN POR REEMPLAZO' || formData.motivoAnulacion === 'ANULACIÓN POR ERROR DE CARGA') && (
                <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl space-y-3 animate-fade-in text-left">
                    <label className="block text-[11px] font-bold text-blue-700 uppercase tracking-wide pl-1 select-none">Vincular Póliza de Reemplazo</label>
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            value={searchTerm} 
                            onChange={(e) => { 
                                setSearchTerm(e.target.value); 
                                setHasSearched(false); 
                            }} 
                            placeholder="Buscar por N° Póliza..." 
                            className="flex-grow border border-slate-200 rounded-xl px-4 py-2 text-sm font-mono text-slate-700 outline-none bg-white h-10 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" 
                        />
                        <button 
                            type="button" 
                            onClick={handleSearchPoliza} 
                            disabled={isSearching}
                            className="bg-blue-600 text-white px-5 rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shrink-0 active:scale-95"
                        >
                            {isSearching ? '...' : 'Buscar'}
                        </button>
                    </div>

                    {/* RESULTADOS DE BÚSQUEDA */}
                    {searchResults.length > 0 && (
                        <div className="bg-white border border-blue-100 rounded-xl overflow-hidden shadow-sm animate-fade-in">
                            {searchResults.map(res => (
                                <button 
                                    key={res.id} 
                                    type="button"
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, polizaReemplazo: { id: res.id, nPoliza: res.nPoliza, compania: res.compania } }));
                                        setSearchResults([]);
                                        setSearchTerm(res.nPoliza);
                                        setHasSearched(false);
                                    }}
                                    className="w-full text-left p-3 hover:bg-blue-50 border-b border-slate-50 last:border-0 text-xs flex justify-between items-center transition-colors"
                                >
                                    <span><strong>Pol: #{res.nPoliza}</strong> - {res.compania}</span>
                                    <span className="text-[10px] bg-blue-100 text-blue-700 px-2.5 py-1 rounded font-black uppercase">Seleccionar</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {hasSearched && searchResults.length === 0 && !formData.polizaReemplazo && (
                        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg animate-fade-in select-none">
                            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                            <span className="text-[10px] text-red-600 font-bold uppercase tracking-wider">Póliza no encontrada en la ficha</span>
                        </div>
                    )}

                    {formData.polizaReemplazo && (
                        <div className="flex items-center justify-between bg-emerald-50/50 p-3 rounded-xl border border-emerald-200 animate-fade-in select-none">
                            <span className="text-xs text-emerald-800 font-bold">✓ Vinculada a Póliza: #{formData.polizaReemplazo.nPoliza} ({formData.polizaReemplazo.compania})</span>
                            <button type="button" onClick={() => setFormData(prev => ({...prev, polizaReemplazo: null}))} className="text-red-500 hover:text-red-700 text-[10px] font-black uppercase">Quitar</button>
                        </div>
                    )}
                </div>
            )}

            {/* FECHA Y QUIÉN GESTIONÓ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Fecha de Anulación *</label>
                    <input type="date" name="fechaAnulacion" value={formData.fechaAnulacion} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[46px] focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all" required />
                </div>
                <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">¿Quién Gestionó? *</label>
                    <select name="quienGestiono" value={formData.quienGestiono} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[46px] focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all" required>
                        <option value="">Seleccione...</option>
                        {sortedColaboradores.map(c => (
                            <option key={c.id} value={c.nombre}>{c.nombre}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* PRIMA ANULACIÓN */}
            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Prima de Anulación (Reintegro / Saldo Final)</label>
                <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] focus-within:border-red-400 focus-within:ring-1 focus-within:ring-red-100 transition-all shadow-sm bg-white">
                    <div className="bg-slate-50 border-r border-slate-100 px-3 h-full flex items-center shrink-0 select-none">
                        <select name="moneda" value={formData.moneda} onChange={handleChange} className="bg-transparent font-bold text-slate-500 outline-none cursor-pointer uppercase text-xs">
                            <option value="ARS $">ARS $</option>
                            <option value="USD U$S">USD u$s</option>
                        </select>
                    </div>
                    <input 
                        type="text" 
                        name="primaAnulacion" 
                        value={formData.primaAnulacion} 
                        onChange={handlePrimaChange} 
                        className="w-full px-4 outline-none font-mono font-bold text-slate-800 placeholder:text-slate-300 h-full text-left" 
                        placeholder="0,00" 
                    />
                </div>
                <p className="text-[10px] text-red-500 mt-2 font-bold italic pl-1 select-none">Se guardará automáticamente como valor negativo (-)</p>
            </div>

            {/* DESPACHO ANULACIÓN */}
            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Despacho de Anulación *</label>
                <select name="despachoAnulacion" value={formData.despachoAnulacion} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white h-[46px] focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all" required>
                    <option value="">Seleccione medio...</option>
                    <option value="Mail">Mail</option>
                    <option value="Whatsapp">Whatsapp</option>
                </select>
            </div>

            {/* OBSERVACIONES */}
            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Observaciones de la Anulación</label>
                <textarea 
                    name="observacionesAnulacion" 
                    value={formData.observacionesAnulacion} 
                    onChange={handleChange} 
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 outline-none bg-white min-h-[100px] resize-none focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all placeholder:text-slate-300" 
                    placeholder="Escriba aquí detalles sobre la entrega de documentación, firmas o solicitudes adicionales..."
                />
            </div>

        </form>

        {/* BOTONERA DE ACCIÓN */}
        <div className="px-6 py-4 bg-gray-50 border-t border-slate-100 flex justify-end gap-3 flex-shrink-0 select-none">
            <button onClick={onClose} className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold hover:bg-slate-100 shadow-sm text-xs uppercase tracking-wider transition-all transform active:scale-95">
                Cancelar
            </button>
            <button 
                type="submit" 
                form="anularForm"
                disabled={isSaving}
                className="px-8 py-2.5 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition shadow-lg shadow-red-100 disabled:opacity-50 text-xs uppercase tracking-wider transform active:scale-95 flex items-center min-w-[180px] justify-center"
            >
                {isSaving ? 'Procesando...' : 'Confirmar Anulación'}
            </button>
        </div>
        
      </div>
    </div>,
    document.body
  );
};

export default ModalAnulacion;